import _ from 'lodash'

import {getDefaultsForJurisdictions, Jurisdiction} from '@/utils/countries/constants'
import {getCountriesAlpha2, getJurisdictionsAlpha2} from '@/utils/countries/helpers'
import {alpha2ToName} from '@/utils/countries/translations'

import {beneluxCountriesCodes, euCountriesCodes, Locale} from './constants'

export enum Region {
  AD = 'AD',
  AE = 'AE',
  AR = 'AR',
  AU = 'AU',
  AT = 'AT',
  BD = 'BD',
  BX = 'BX',
  BR = 'BR',
  BG = 'BG',
  CA = 'CA',
  CN = 'CN',
  CO = 'CO',
  HR = 'HR',
  CZ = 'CZ',
  DK = 'DK',
  EC = 'EC',
  EE = 'EE',
  EG = 'EG',
  EU = 'EU',
  FI = 'FI',
  FR = 'FR',
  GE = 'GE',
  DE = 'DE',
  HK = 'HK',
  HU = 'HU',
  IN = 'IN',
  ID = 'ID',
  IE = 'IE',
  IL = 'IL',
  IT = 'IT',
  JO = 'JO',
  JP = 'JP',
  KE = 'KE',
  KZ = 'KZ',
  LB = 'LB',
  LV = 'LV',
  LT = 'LT',
  MA = 'MA',
  MC = 'MC',
  MX = 'MX',
  MY = 'MY',
  NZ = 'NZ',
  NG = 'NG',
  NO = 'NO',
  PE = 'PE',
  PH = 'PH',
  PK = 'PK',
  PL = 'PL',
  PT = 'PT',
  RO = 'RO',
  SA = 'SA',
  SG = 'SG',
  SK = 'SK',
  SI = 'SI',
  ZA = 'ZA',
  KR = 'KR',
  ES = 'ES',
  SE = 'SE',
  CH = 'CH',
  TW = 'TW',
  TH = 'TH',
  TN = 'TN',
  TR = 'TR',
  TZ = 'TZ',
  UA = 'UA',
  GB = 'GB',
  US = 'US',
  VN = 'VN',
}

export enum Domain {
  COM = 'https://tramatm.com',
  SK = 'https://tramatm.sk',
  CZ = 'https://tramatm.cz',
  UK = 'https://tramatm.co.uk',
  IE = 'https://www.tramatm.ie',
  CH = 'https://tramatm.ch',
  AU = 'https://tramatm.com.au',
}

export const regionToIPOffice = {
  [Region.EU]: 'EUIPO',
  [Region.US]: 'USPTO',
  [Region.GB]: 'UKIPO',
}

export const tldToRegion: Record<string, Region> = {
  sk: Region.SK,
  cz: Region.CZ,
  uk: Region.GB,
  ie: Region.IE,
  ch: Region.CH,
  au: Region.AU,
}

export const tldToDomain: Record<string, Domain> = {
  com: Domain.COM,
  sk: Domain.SK,
  cz: Domain.CZ,
  uk: Domain.UK,
  ie: Domain.IE,
  ch: Domain.CH,
  au: Domain.AU,
}

export const tldToCountry: Record<string, Jurisdiction> = {
  sk: Jurisdiction.SK,
  cz: Jurisdiction.CZ,
  uk: Jurisdiction.UK,
  ie: Jurisdiction.IE,
  ch: Jurisdiction.CH,
  au: Jurisdiction.AU,
}

export const regionToDomain: Partial<Record<Region, Domain>> = {
  SK: Domain.SK,
  CZ: Domain.CZ,
  GB: Domain.UK,
  IE: Domain.IE,
  CH: Domain.CH,
  AU: Domain.AU,
}

export const domainLabels = {
  [Domain.COM]: {
    label: 'tramatm.com',
    icon: '/images/icons/globe-on-dark-bg.svg',
  },
  [Domain.SK]: {
    label: 'tramatm.sk',
    icon: '/images/flags/sk-flag.svg',
  },
  [Domain.CZ]: {
    label: 'tramatm.cz',
    icon: '/images/flags/cz-flag.svg',
  },
  [Domain.UK]: {
    label: 'tramatm.co.uk',
    icon: '/images/flags/uk-flag.svg',
  },
  [Domain.IE]: {
    label: 'tramatm.ie',
    icon: '/images/flags/ie-flag.svg',
  },
  [Domain.CH]: {
    label: 'tramatm.ch',
    icon: '/images/flags/ch-flag.svg',
  },
  [Domain.AU]: {
    label: 'tramatm.com.au',
    icon: '/images/flags/au-flag.svg',
  },
}

export const countryToRegion = (country?: string | null): Region => {
  if (country) {
    if (country in Region) return Region[country as Region]
    if (beneluxCountriesCodes.includes(country)) return Region.BX
    if (euCountriesCodes.includes(country)) return Region.EU
  }
  return Region.US
}

// A country code as Cloudflare reports it; its XX (unknown) and T1 (Tor) are not countries
export const parseCountry = (value: string | null | undefined) =>
  value && /^[A-Z]{2}$/.test(value) && value !== 'XX' && value !== 'T1' ? value : undefined

export const getTLD = (host: string): string | null =>
  host.split('.').reverse()?.[0].replace('/', '') || null

export const validateRegion = (region?: string | null | object): Region | undefined => {
  if (!region || typeof region !== 'string') return undefined

  if (region.toUpperCase() === 'UK') return Region.GB

  return Region[region.toUpperCase() as Region] ?? undefined
}

export const validateCountryLandingPageParam = (
  countryLPparam?: string | null,
): Region | undefined => {
  if (!countryLPparam) return undefined

  if (countryLPparam.toUpperCase() === 'UK') return Region.GB

  return ['US', 'CA', 'EU', 'CZ', 'AU'].includes(countryLPparam)
    ? (countryLPparam as Region)
    : undefined
}

export const getDefaultOrigin = (
  host: string,
  cookieValue?: string,
  paramValue?: string,
  country?: string,
): Region => {
  if (paramValue) {
    if (paramValue in Region) {
      return paramValue as Region
    }
    if (paramValue.toUpperCase() === 'UK') {
      return Region.GB
    }
  }
  const topLevelDomain = getTLD(host)
  if (topLevelDomain && topLevelDomain in tldToRegion) return tldToRegion[topLevelDomain]
  if (cookieValue && cookieValue in Region) return cookieValue as Region
  return countryToRegion(country)
}

export const recommendedSelectionAddressCountry = {
  [Locale.sk]: [{value: 'SK', label: 'Slovensko'}],
  [Locale.cs]: [{value: 'CZ', label: 'Česká republika'}],
  [Locale.en]: [
    {value: 'GB', label: 'United Kingdom'},
    {value: 'US', label: 'United States of America'},
  ],
}

export type CountryOption = {
  value: string
  label: string
}

export const emptyCountryOption = {value: '', label: '-'}

export const usStates = [
  {name: 'Alabama', code: 'AL'},
  {name: 'Alaska', code: 'AK'},
  {name: 'American Samoa', code: 'AS'},
  {name: 'Arizona', code: 'AZ'},
  {name: 'Arkansas', code: 'AR'},
  {name: 'California', code: 'CA'},
  {name: 'Colorado', code: 'CO'},
  {name: 'Connecticut', code: 'CT'},
  {name: 'Delaware', code: 'DE'},
  {name: 'District Of Columbia', code: 'DC'},
  {name: 'Federated States Of Micronesia', code: 'FM'},
  {name: 'Florida', code: 'FL'},
  {name: 'Georgia', code: 'GA'},
  {name: 'Guam', code: 'GU'},
  {name: 'Hawaii', code: 'HI'},
  {name: 'Idaho', code: 'ID'},
  {name: 'Illinois', code: 'IL'},
  {name: 'Indiana', code: 'IN'},
  {name: 'Iowa', code: 'IA'},
  {name: 'Kansas', code: 'KS'},
  {name: 'Kentucky', code: 'KY'},
  {name: 'Louisiana', code: 'LA'},
  {name: 'Maine', code: 'ME'},
  {name: 'Marshall Islands', code: 'MH'},
  {name: 'Maryland', code: 'MD'},
  {name: 'Massachusetts', code: 'MA'},
  {name: 'Michigan', code: 'MI'},
  {name: 'Minnesota', code: 'MN'},
  {name: 'Mississippi', code: 'MS'},
  {name: 'Missouri', code: 'MO'},
  {name: 'Montana', code: 'MT'},
  {name: 'Nebraska', code: 'NE'},
  {name: 'Nevada', code: 'NV'},
  {name: 'New Hampshire', code: 'NH'},
  {name: 'New Jersey', code: 'NJ'},
  {name: 'New Mexico', code: 'NM'},
  {name: 'New York', code: 'NY'},
  {name: 'North Carolina', code: 'NC'},
  {name: 'North Dakota', code: 'ND'},
  {name: 'Northern Mariana Islands', code: 'MP'},
  {name: 'Ohio', code: 'OH'},
  {name: 'Oklahoma', code: 'OK'},
  {name: 'Oregon', code: 'OR'},
  {name: 'Palau', code: 'PW'},
  {name: 'Pennsylvania', code: 'PA'},
  {name: 'Puerto Rico', code: 'PR'},
  {name: 'Rhode Island', code: 'RI'},
  {name: 'South Carolina', code: 'SC'},
  {name: 'South Dakota', code: 'SD'},
  {name: 'Tennessee', code: 'TN'},
  {name: 'Texas', code: 'TX'},
  {name: 'Utah', code: 'UT'},
  {name: 'Vermont', code: 'VT'},
  {name: 'Virgin Islands', code: 'VI'},
  {name: 'Virginia', code: 'VA'},
  {name: 'Washington', code: 'WA'},
  {name: 'West Virginia', code: 'WV'},
  {name: 'Wisconsin', code: 'WI'},
  {name: 'Wyoming', code: 'WY'},
]

const usStatesOptions: CountryOption[] = usStates.map((item) => ({
  value: item.name,
  label: item.name,
}))

const canadaProvincesAndTerritories = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Nova Scotia',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Northwest Territories',
  'Nunavut',
  'Yukon',
]

const canadaStatesOptions: CountryOption[] = canadaProvincesAndTerritories.map((item) => ({
  value: item,
  label: item,
}))

// Codes as IP Australia lists them (GET /reference-data/au-states); addresses store the names
export const australiaStates = [
  {name: 'Australian Capital Territory', code: 'ACT'},
  {name: 'New South Wales', code: 'NSW'},
  {name: 'Northern Territory', code: 'NT'},
  {name: 'Queensland', code: 'QLD'},
  {name: 'South Australia', code: 'SA'},
  {name: 'Tasmania', code: 'TAS'},
  {name: 'Victoria', code: 'VIC'},
  {name: 'Western Australia', code: 'WA'},
]

const australiaStatesOptions: CountryOption[] = australiaStates.map((item) => ({
  value: item.name,
  label: item.name,
}))

export const getStateOptionsByCountry = (country: string | null | undefined): CountryOption[] => {
  switch (country) {
    case Jurisdiction.US:
      return usStatesOptions
    case Jurisdiction.CA:
      return canadaStatesOptions
    case Jurisdiction.AU:
      return australiaStatesOptions
    default:
      return []
  }
}

export const isCountryWithState = (country: string | null | undefined): boolean =>
  country === Jurisdiction.US || country === Jurisdiction.CA || country === Jurisdiction.AU

export const offeredCountriesOptions = (
  language: Locale,
  countryList?: string[],
): CountryOption[] => {
  const list = countryList || getJurisdictionsAlpha2()
  const allCountries = list.map((country) => ({
    value: country,
    label: alpha2ToName(country, language),
  }))
  const collator = new Intl.Collator(language, {numeric: false})
  return allCountries.sort((a, b) => collator.compare(a.label, b.label))
}

export const availableCountries = (
  language: string,
  recommendedSelection?: Record<string, Array<CountryOption>>,
): Array<CountryOption> => {
  const collator = new Intl.Collator(language, {numeric: false})

  let countries = _(getCountriesAlpha2())
    .map((alpha2) => ({value: alpha2, label: alpha2ToName(alpha2, language as Locale)}))
    .value()
    .sort((a, b) => collator.compare(a.label, b.label))

  if (recommendedSelection !== undefined) {
    if (recommendedSelection[language] !== undefined) {
      const codesOfSelection = _.map(recommendedSelection[language], (country) => country.value)
      countries = countries.filter((o) => {
        return codesOfSelection.indexOf(o.value) < 0
      })
      countries.unshift(...recommendedSelection[language])
    }
  }

  return countries
}

export const officeNames: Record<Jurisdiction, string> = {
  ...getDefaultsForJurisdictions<string>('Intellectual Property Office'),
  US: 'US Patent and Trademark Office',
  EU: 'European Union Intellectual Property Office',
  UK: 'United Kingdom Intellectual Property Office',
  AU: 'IP Australia',
  NZ: 'Intellectual Property Office of New Zealand',
  CA: 'Canadian Intellectual Property Office',
  CN: 'China National Intellectual Property Administration',
  IN: 'Indian Patent Office',
}
